/* eslint-disable array-callback-return */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';

import Toolbar from '../../components/Toolbar';
import LoadingAnimation from '../../components/Loading';
import api from '../../services/api';
import formatValue from '../../utils/formatValue';

import {
  Container,
  Form,
  Content,
  HeaderRow,
  Title,
  ChartsGrid,
  ChartsStack,
  ChartCard,
  ChartTitle,
  ControlsRow,
  Select,
} from './styles';

type ApiItem = {
  account_id: string;
  account_type: 'IN' | 'OUT';
  account_name: string;
  total_amount: number; // cents
};

type ApiResponse = Record<string, ApiItem[]>; // key: YYYY-MM

// High-contrast palette tuned for dark backgrounds
const palette = [
  '#00E5FF', // cyan
  '#FF5722', // deep orange
  '#FF5252', // red
  '#7C4DFF', // deep purple
  '#00E676', // bright green
  '#FFD600', // vivid yellow
  '#2979FF', // bright blue
  '#FF9100', // orange
  '#00BFA5', // teal
  '#C51162', // magenta
  '#64DD17', // lime
  '#D500F9', // purple
];

const Analytics: React.FC = () => {
  const [months, setMonths] = useState<number>(6);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ApiResponse>({});

  // Visibility toggles per account (by name) for IN/OUT charts
  const [visibleOut, setVisibleOut] = useState<Record<string, boolean>>({});
  const [visibleIn, setVisibleIn] = useState<Record<string, boolean>>({});

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<ApiResponse>('transaction/monthly-sum', { params: { months } })
      .then((res) => {
        const payload = res.data || {};
        setData(payload);
        // initialize visibility from discovered accounts
        const accOut = new Set<string>();
        const accIn = new Set<string>();
        Object.values(payload).forEach((arr) => {
          arr.forEach((it) => {
            if (it.account_type === 'OUT') accOut.add(it.account_name);
            if (it.account_type === 'IN') accIn.add(it.account_name);
          });
        });
        const vOut: Record<string, boolean> = {};
        accOut.forEach((n) => (vOut[n] = true));
        const vIn: Record<string, boolean> = {};
        accIn.forEach((n) => (vIn[n] = true));
        setVisibleOut(vOut);
        setVisibleIn(vIn);
      })
      .finally(() => setLoading(false));
  }, [months]);

  useEffect(() => {
    load();
  }, [load]);

  const monthsSorted = useMemo(() => {
    // sort by key (YYYY-MM)
    const keys = Object.keys(data);
    return keys.sort();
  }, [data]);

  const outAccounts = useMemo(() => {
    const set = new Set<string>();
    monthsSorted.forEach((m) => {
      (data[m] || []).forEach((it) => {
        if (it.account_type === 'OUT') set.add(it.account_name);
      });
    });
    return Array.from(set.values()).sort();
  }, [data, monthsSorted]);

  const inAccounts = useMemo(() => {
    const set = new Set<string>();
    monthsSorted.forEach((m) => {
      (data[m] || []).forEach((it) => {
        if (it.account_type === 'IN') set.add(it.account_name);
      });
    });
    return Array.from(set.values()).sort();
  }, [data, monthsSorted]);

  const outLineData = useMemo(() => {
    return monthsSorted.map((m) => {
      const base: Record<string, number> = {};
      outAccounts.forEach((name) => {
        base[name] = 0;
      });
      (data[m] || []).forEach((it) => {
        if (it.account_type === 'OUT') {
          base[it.account_name] = (base[it.account_name] || 0) + it.total_amount / 100;
        }
      });
      return { month: m, ...base } as any;
    });
  }, [data, monthsSorted, outAccounts]);

  const inLineData = useMemo(() => {
    return monthsSorted.map((m) => {
      const base: Record<string, number> = {};
      inAccounts.forEach((name) => {
        base[name] = 0;
      });
      (data[m] || []).forEach((it) => {
        if (it.account_type === 'IN') {
          base[it.account_name] = (base[it.account_name] || 0) + it.total_amount / 100;
        }
      });
      return { month: m, ...base } as any;
    });
  }, [data, monthsSorted, inAccounts]);

  const stackedOutcomeData = outLineData; // same shape: per account by month
  const lastVisibleOutIdx = useMemo(() => {
    let idx = -1;
    outAccounts.forEach((name, i) => {
      if (visibleOut[name] !== false) idx = i;
    });
    return idx;
  }, [outAccounts, visibleOut]);
  const incomeVsOutcome = useMemo(() => {
    return monthsSorted.map((m) => {
      let income = 0;
      let outcome = 0;
      (data[m] || []).forEach((it) => {
        if (it.account_type === 'IN') income += it.total_amount / 100;
        if (it.account_type === 'OUT') outcome += it.total_amount / 100;
      });
      return { month: m, Income: income, Outcome: outcome };
    });
  }, [data, monthsSorted]);

  const colorFor = (idx: number) => palette[idx % palette.length];
  const tooltipFormatter = (value: any) => formatValue(Number(value));

  // Dark tooltip content that uses each payload item's color
  const DarkTooltip: React.FC<any> = ({ active, label, payload, formatter }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div
        style={{
          background: '#1E1E1E',
          border: '1px solid rgba(255,255,255,0.1)',
          color: '#EAEAEA',
          padding: 8,
          borderRadius: 6,
        }}
      >
        {label && (
          <div style={{ color: '#A0A0A0', marginBottom: 6, fontSize: 12 }}>{label}</div>
        )}
        {payload.map((entry: any, idx: number) => {
          const val = entry && entry.value != null ? entry.value : 0;
          const name = entry && entry.name ? entry.name : '';
          const color = entry && entry.color ? entry.color : '#EAEAEA';
          const formatted = formatter ? formatter(val, name, entry) : val;
          return (
            <div key={`item-${idx}`} style={{ color, fontSize: 13, lineHeight: '18px' }}>
              {name}: {formatted}
            </div>
          );
        })}
      </div>
    );
  };

  const onLegendClickOut = (e: any) => {
    const key = e?.dataKey as string;
    if (!key) return;
    setVisibleOut((curr) => {
      const keys = Object.keys(curr);
      const visibleKeys = keys.filter((k) => curr[k] !== false);
      const onlyThisVisible = visibleKeys.length === 1 && visibleKeys[0] === key;
      if (onlyThisVisible) {
        // Restore all
        const all: Record<string, boolean> = {};
        keys.forEach((k) => (all[k] = true));
        return all;
      }
      // Solo the clicked series
      const next: Record<string, boolean> = {};
      keys.forEach((k) => (next[k] = k === key));
      return next;
    });
  };
  const onLegendClickIn = (e: any) => {
    const key = e?.dataKey as string;
    if (!key) return;
    setVisibleIn((curr) => {
      const keys = Object.keys(curr);
      const visibleKeys = keys.filter((k) => curr[k] !== false);
      const onlyThisVisible = visibleKeys.length === 1 && visibleKeys[0] === key;
      if (onlyThisVisible) {
        const all: Record<string, boolean> = {};
        keys.forEach((k) => (all[k] = true));
        return all;
      }
      const next: Record<string, boolean> = {};
      keys.forEach((k) => (next[k] = k === key));
      return next;
    });
  };

  return (
    <Container>
      <Toolbar />
      <Form onSubmit={() => {}}>{/* spacer to offset toolbar */}</Form>

      <Content>
        <HeaderRow>
          <Title>Analytics</Title>
          <ControlsRow>
            <label htmlFor="months">Meses:</label>
            <Select
              id="months"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => i + 3).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </ControlsRow>
        </HeaderRow>

        {loading ? (
          <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingAnimation />
          </div>
        ) : (
          <>
            <ChartsStack>
              <ChartCard>
                <ChartTitle>Saídas por mês (por conta)</ChartTitle>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={outLineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="month" stroke="#A0A0A0" />
                    <YAxis stroke="#A0A0A0" />
                    <ReTooltip content={<DarkTooltip formatter={tooltipFormatter} />} />
                    <Legend onClick={onLegendClickOut} />
                    {outAccounts.map((name, idx) => (
                      <Line key={name} type="monotone" dot={false} strokeWidth={2} dataKey={name} stroke={colorFor(idx)} hide={visibleOut[name] === false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard>
                <ChartTitle>Entradas por mês (por conta)</ChartTitle>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={inLineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="month" stroke="#A0A0A0" />
                    <YAxis stroke="#A0A0A0" />
                    <ReTooltip content={<DarkTooltip formatter={tooltipFormatter} />} />
                    <Legend onClick={onLegendClickIn} />
                    {inAccounts.map((name, idx) => (
                      <Line key={name} type="monotone" dot={false} strokeWidth={2} dataKey={name} stroke={colorFor(idx)} hide={visibleIn[name] === false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsStack>

            <ChartsGrid>
              <ChartCard>
                <ChartTitle>Saídas por categoria (empilhado)</ChartTitle>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stackedOutcomeData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="month" stroke="#A0A0A0" />
                    <YAxis stroke="#A0A0A0" />
                    <ReTooltip content={<DarkTooltip formatter={tooltipFormatter} />} cursor={{ fill: 'rgba(255,255,255,0.06)' }} />
                    <Legend onClick={onLegendClickOut} />
                    {outAccounts.map((name, idx) => (
                      <Bar
                        key={name}
                        dataKey={name}
                        stackId="out"
                        fill={colorFor(idx)}
                        hide={visibleOut[name] === false}
                        radius={idx === lastVisibleOutIdx ? [6, 6, 0, 0] : 0}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard>
                <ChartTitle>Entradas vs Saídas (empilhado)</ChartTitle>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={incomeVsOutcome} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="month" stroke="#A0A0A0" />
                    <YAxis stroke="#A0A0A0" />
                    <ReTooltip content={<DarkTooltip formatter={tooltipFormatter} />} cursor={{ fill: 'rgba(255,255,255,0.06)' }} />
                    <Legend />
                    <Bar dataKey="Income" name="Entradas" stackId="io" fill="#00E676" />
                    {/* Top of the stack only: Outcome is rendered last */}
                    <Bar dataKey="Outcome" name="Saídas" stackId="io" fill="#FF5252" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsGrid>
          </>
        )}
      </Content>
    </Container>
  );
};

export default Analytics;
